import React, { useState, useEffect } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, ActivityIndicator, TextInput, Image } from 'react-native';
import { useTheme } from '../contexts/ThemeContext';
import { auth, storage } from '../firebaseConfig';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { getGenerationMetrics, calculateMetricsStats, formatTime } from '../services/metricsService';
import { getUserProfile } from '../services/profileService';
import { getPartnerLogos, addPartnerLogo, updatePartnerLogo, deletePartnerLogo, getCarouselSettings, updateCarouselSettings } from '../services/firestoreService';
import LandingStyleHeader from '../components/LandingStyleHeader';
import Toast from '../components/Toast';

export default function AdminScreen({ navigation }) {
  const { colors, glass, isDark, gradients } = useTheme();
  const [loading, setLoading] = useState(true);
  const [metrics, setMetrics] = useState([]);
  const [stats, setStats] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [activeTab, setActiveTab] = useState('metrics');
  const [partnerLogos, setPartnerLogos] = useState([]);
  const [newLogo, setNewLogo] = useState({ name: '', url: '', order: 0 });
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [carouselSpeed, setCarouselSpeed] = useState(40);
  const [logoSize, setLogoSize] = useState(120);
  const [logoSpacing, setLogoSpacing] = useState(60);
  const [toast, setToast] = useState({ visible: false, message: '', type: 'success' });

  useEffect(() => {
    document.title = 'Flomodo - Admin';
    checkAdminAccess();
  }, []);

  const showToast = (message, type = 'success') => {
    setToast({ visible: true, message, type });
  };

  const checkAdminAccess = async () => {
    try {
      const userProfile = await getUserProfile(auth.currentUser.uid);
      const hasAccess = userProfile?.userType === 'admin';
      setIsAdmin(hasAccess);
      
      if (hasAccess) {
        loadMetrics();
      } else {
        setLoading(false);
      }
    } catch (error) {
      console.error('Error checking admin access:', error);
      setIsAdmin(false);
      setLoading(false);
    }
  };

  const loadMetrics = async () => {
    setLoading(true);
    try {
      const metricsData = await getGenerationMetrics();
      setMetrics(metricsData);
      const calculatedStats = calculateMetricsStats(metricsData);
      setStats(calculatedStats);
    } catch (error) {
      console.error('Error loading metrics:', error);
      alert('Error loading metrics: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const loadPartnerLogos = async () => {
    try {
      const logos = await getPartnerLogos();
      setPartnerLogos(logos);
    } catch (error) {
      console.error('Error loading partner logos:', error);
    }
  };

  const loadCarouselSettings = async () => {
    try {
      const settings = await getCarouselSettings();
      setCarouselSpeed(settings.speed || 40);
      setLogoSize(settings.logoSize || 120);
      setLogoSpacing(settings.logoSpacing || 60);
    } catch (error) {
      console.error('Error loading carousel settings:', error);
    }
  };

  const handleUpdateCarouselSettings = async () => {
    try {
      await updateCarouselSettings({
        speed: carouselSpeed,
        logoSize: logoSize,
        logoSpacing: logoSpacing
      });
      showToast('Carousel settings updated!', 'success');
    } catch (error) {
      console.error('Error updating carousel settings:', error);
      showToast('Error updating carousel settings', 'error');
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        alert('Please select an image file');
        return;
      }
      
      // Validate file size (max 2MB)
      if (file.size > 2 * 1024 * 1024) {
        alert('File size must be less than 2MB');
        return;
      }
      
      setSelectedFile(file);
      // Clear URL field if file is selected
      setNewLogo({ ...newLogo, url: '' });
    }
  };

  const uploadLogoImage = async (file) => {
    const storageRef = ref(storage, `partner-logos/${Date.now()}-${file.name}`);
    await uploadBytes(storageRef, file);
    const url = await getDownloadURL(storageRef);
    return url;
  };

  const handleAddLogo = async () => {
    if (!newLogo.name || (!newLogo.url && !selectedFile)) {
      showToast('Please provide a name and either upload an image or enter a URL', 'error');
      return;
    }

    setUploadingLogo(true);
    try {
      let logoUrl = newLogo.url;
      
      // If file is selected, upload it first
      if (selectedFile) {
        logoUrl = await uploadLogoImage(selectedFile);
      }

      await addPartnerLogo({
        name: newLogo.name,
        url: logoUrl,
        order: newLogo.order
      });
      
      setNewLogo({ name: '', url: '', order: 0 });
      setSelectedFile(null);
      await loadPartnerLogos();
      showToast('Logo added successfully!', 'success');
    } catch (error) {
      console.error('Error adding logo:', error);
      showToast('Error adding logo: ' + error.message, 'error');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleDeleteLogo = async (logoId) => {
    if (!confirm('Are you sure you want to delete this logo?')) return;

    try {
      await deletePartnerLogo(logoId);
      await loadPartnerLogos();
      showToast('Logo deleted successfully', 'success');
    } catch (error) {
      console.error('Error deleting logo:', error);
      showToast('Error deleting logo: ' + error.message, 'error');
    }
  };

  useEffect(() => {
    if (activeTab === 'siteManagement') {
      loadPartnerLogos();
      loadCarouselSettings();
    }
  }, [activeTab]);

  if (!isAdmin && !loading) {
    return (
      <View style={[styles.container, { backgroundImage: isDark ? gradients.background : 'none', backgroundColor: colors.primary }]}>
        <LandingStyleHeader navigation={navigation} currentScreen="admin" />
        <View style={styles.accessDenied}>
          <Text style={styles.accessDeniedTitle}>Access Denied</Text>
          <Text style={styles.accessDeniedText}>
            You don't have permission to view this page.
          </Text>
          <TouchableOpacity 
            style={styles.backButton}
            onPress={() => navigation.navigate('wizard')}
          >
            <Text style={styles.backButtonText}>← Back to Home</Text>
          </TouchableOpacity>
        </View>
      </View>
    );
  }

  if (loading) {
    return (
      <View style={[styles.container, { backgroundImage: isDark ? gradients.background : 'none', backgroundColor: colors.primary }]}>
        <LandingStyleHeader navigation={navigation} currentScreen="admin" />
        <View style={[styles.loadingContainer, { backgroundColor: colors.primary }]}>
          <ActivityIndicator size="large" color="#4a9eff" />
          <Text style={styles.loadingText}>Loading metrics...</Text>
        </View>
      </View>
    );
  }

  return (
    <View style={[styles.container, { backgroundImage: isDark ? gradients.background : 'none', backgroundColor: colors.primary }]}>
      <LandingStyleHeader navigation={navigation} currentScreen="admin" />
      
      <View style={styles.contentContainer}>
        <View style={styles.titleSection}>
          <Text style={[styles.title, { color: colors.text }]}>Admin Dashboard</Text>
        </View>

        {/* Tab Navigation */}
        <View style={[styles.tabContainer, { backgroundColor: colors.secondary, borderColor: colors.border }]}>
          <TouchableOpacity
            style={[
              styles.tab,
              activeTab === 'metrics' && [styles.activeTab, { backgroundColor: colors.accent }]
            ]}
            onPress={() => setActiveTab('metrics')}
          >
            <Text style={[styles.tabText, activeTab === 'metrics' && { color: '#fff' }, { color: colors.text }]}>
              Metrics
            </Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[
              styles.tab,
              activeTab === 'siteManagement' && [styles.activeTab, { backgroundColor: colors.accent }]
            ]}
            onPress={() => setActiveTab('siteManagement')}
          >
            <Text style={[styles.tabText, activeTab === 'siteManagement' && { color: '#fff' }, { color: colors.text }]}>
              Site Management
            </Text>
          </TouchableOpacity>
        </View>

        {activeTab === 'metrics' && (
          <>
            <TouchableOpacity style={[styles.refreshButton, { backgroundColor: colors.accent }]} onPress={loadMetrics}>
              <Text style={styles.refreshButtonText}>Refresh Metrics</Text>
            </TouchableOpacity>

        {stats && (
          <>
            {/* Main Stats Grid */}
            <View style={styles.statsGrid}>
              <View style={[styles.statCard, { ...glass, backgroundColor: colors.card, borderColor: colors.border }]}>
                <Text style={[styles.statValue, { color: colors.accent }]}>{stats.totalGenerations}</Text>
                <Text style={[styles.statLabel, { color: colors.textSecondary }]}>Total Generations</Text>
              </View>
              
              <View style={[styles.statCard, { ...glass, backgroundColor: colors.card, borderColor: colors.border }]}>
                <Text style={[styles.statValue, { color: colors.accent }]}>{formatTime(stats.averageTime)}</Text>
                <Text style={[styles.statLabel, { color: colors.textSecondary }]}>Average Time</Text>
              </View>
              
              <View style={[styles.statCard, { ...glass, backgroundColor: colors.card, borderColor: colors.border }]}>
                <Text style={[styles.statValue, { color: colors.accent }]}>{formatTime(stats.medianTime)}</Text>
                <Text style={[styles.statLabel, { color: colors.textSecondary }]}>Median Time</Text>
              </View>
              
              <View style={[styles.statCard, { ...glass, backgroundColor: colors.card, borderColor: colors.border }]}>
                <Text style={[styles.statValue, { color: colors.accent }]}>{stats.last24Hours}</Text>
                <Text style={[styles.statLabel, { color: colors.textSecondary }]}>Last 24 Hours</Text>
              </View>
              
              <View style={[styles.statCard, { ...glass, backgroundColor: colors.card, borderColor: colors.border }]}>
                <Text style={[styles.statValue, { color: colors.accent }]}>{stats.last7Days}</Text>
                <Text style={[styles.statLabel, { color: colors.textSecondary }]}>Last 7 Days</Text>
              </View>
              
              <View style={[styles.statCard, { ...glass, backgroundColor: colors.card, borderColor: colors.border }]}>
                <Text style={[styles.statValue, { color: colors.accent }]}>{formatTime(stats.minTime)}</Text>
                <Text style={[styles.statLabel, { color: colors.textSecondary }]}>Fastest</Text>
              </View>
              
              <View style={[styles.statCard, { ...glass, backgroundColor: colors.card, borderColor: colors.border }]}>
                <Text style={[styles.statValue, { color: colors.accent }]}>{formatTime(stats.maxTime)}</Text>
                <Text style={[styles.statLabel, { color: colors.textSecondary }]}>Slowest</Text>
              </View>
            </View>

            {/* Recent Generations */}
            <View style={styles.section}>
              <Text style={[styles.sectionTitle, { color: colors.text }]}>Recent Generations</Text>
              <View style={[styles.tableContainer, { ...glass, backgroundColor: colors.card, borderColor: colors.border }]}>
                <View style={[styles.tableHeader, { backgroundColor: colors.secondary, borderBottomColor: colors.border }]}>
                  <Text style={[styles.tableHeaderText, { color: colors.text, flex: 2 }]}>User</Text>
                  <Text style={[styles.tableHeaderText, { color: colors.text, flex: 1 }]}>Time</Text>
                  <Text style={[styles.tableHeaderText, { color: colors.text, flex: 1 }]}>Examples</Text>
                  <Text style={[styles.tableHeaderText, { color: colors.text, flex: 1 }]}>Status</Text>
                  <Text style={[styles.tableHeaderText, { color: colors.text, flex: 2 }]}>Date</Text>
                </View>
                
                {metrics.slice(0, 20).map((metric, index) => (
                  <View key={metric.id || index} style={[styles.tableRow, { borderBottomColor: colors.border }]}>
                    <Text style={[styles.tableCell, { color: colors.text, flex: 2 }]} numberOfLines={1}>
                      {metric.userEmail?.split('@')[0] || 'Unknown'}
                    </Text>
                    <Text style={[styles.tableCell, { color: colors.textSecondary, flex: 1 }]}>
                      {formatTime(metric.elapsedTime || 0)}
                    </Text>
                    <Text style={[styles.tableCell, { color: colors.textSecondary, flex: 1 }]}>
                      {metric.exampleCount || 0}
                    </Text>
                    <Text style={[styles.tableCell, { color: metric.status === 'success' ? colors.accent : colors.error, flex: 1 }]}>
                      {metric.status === 'success' ? 'Success' : 'Error'}
                    </Text>
                    <Text style={[styles.tableCell, { color: colors.textSecondary, flex: 2 }]} numberOfLines={1}>
                      {new Date(metric.timestamp).toLocaleString()}
                    </Text>
                  </View>
                ))}
              </View>
            </View>

            {/* Performance Insights */}
            <View style={styles.section}>
              <Text style={[styles.sectionTitle, { color: colors.text }]}>Insights</Text>
              <View style={[styles.insightCard, { ...glass, backgroundColor: colors.card, borderColor: colors.border }]}>
                <Text style={[styles.insightText, { color: colors.text }]}>
                  • Average generation time is{' '}
                  <Text style={[styles.insightHighlight, { color: colors.accent }]}>{formatTime(stats.averageTime)}</Text>
                </Text>
                <Text style={[styles.insightText, { color: colors.text }]}>
                  • {stats.last24Hours} proposals generated in the last 24 hours
                </Text>
                <Text style={[styles.insightText, { color: colors.text }]}>
                  • Fastest generation:{' '}
                  <Text style={[styles.insightHighlight, { color: colors.accent }]}>{formatTime(stats.minTime)}</Text>
                </Text>
                <Text style={[styles.insightText, { color: colors.text }]}>
                  • Success rate:{' '}
                  <Text style={[styles.insightHighlight, { color: colors.accent }]}>
                    {stats.totalGenerations > 0 
                      ? Math.round((metrics.filter(m => m.status === 'success').length / stats.totalGenerations) * 100)
                      : 0}%
                  </Text>
                </Text>
              </View>
            </View>
          </>
        )}
        </>
        )}

        {/* Site Management Tab */}
        {activeTab === 'siteManagement' && (
          <View style={styles.siteManagementContainer}>
            {/* Add New Logo Form - More Visual */}
            <View style={[styles.addLogoCard, { ...glass, backgroundColor: colors.card, borderColor: colors.border }]}>
              <View style={styles.addLogoHeader}>
                <View style={[styles.addIconContainer, { backgroundColor: colors.accent }]}>
                  <Text style={styles.addIcon}>+</Text>
                </View>
                <View style={styles.addLogoHeaderText}>
                  <Text style={[styles.addLogoTitle, { color: colors.text }]}>Add Partner Logo</Text>
                  <Text style={[styles.addLogoSubtitle, { color: colors.textSecondary }]}>Upload or paste URL</Text>
                </View>
              </View>

              <View style={styles.visualFormGrid}>
                {/* Logo Name */}
                <View style={styles.visualFormField}>
                  <TextInput
                    style={[styles.visualInput, { backgroundColor: colors.secondary, borderColor: colors.border, color: colors.text }]}
                    placeholder="Partner Name"
                    placeholderTextColor={colors.textTertiary}
                    value={newLogo.name}
                    onChangeText={(text) => setNewLogo({ ...newLogo, name: text })}
                  />
                </View>

                {/* Display Order */}
                <View style={styles.visualFormField}>
                  <TextInput
                    style={[styles.visualInput, { backgroundColor: colors.secondary, borderColor: colors.border, color: colors.text }]}
                    placeholder="Order (0-99)"
                    placeholderTextColor={colors.textTertiary}
                    keyboardType="numeric"
                    value={String(newLogo.order)}
                    onChangeText={(text) => setNewLogo({ ...newLogo, order: parseInt(text) || 0 })}
                  />
                </View>
              </View>

              {/* Image Upload Area */}
              <View style={[styles.uploadArea, { backgroundColor: colors.secondary, borderColor: colors.border }]}>
                {selectedFile ? (
                  <View style={styles.uploadedFileInfo}>
                    <Text style={[styles.uploadIcon, { color: colors.accent }]}>📁</Text>
                    <Text style={[styles.uploadedFileName, { color: colors.text }]}>{selectedFile.name}</Text>
                    <TouchableOpacity onPress={() => setSelectedFile(null)}>
                      <Text style={[styles.removeFileBtn, { color: colors.error }]}>✕</Text>
                    </TouchableOpacity>
                  </View>
                ) : (
                  <View style={styles.uploadPlaceholder}>
                    <Text style={[styles.uploadIcon, { color: colors.textTertiary }]}>📤</Text>
                    <Text style={[styles.uploadText, { color: colors.textSecondary }]}>Click to upload image</Text>
                    <Text style={[styles.uploadHint, { color: colors.textTertiary }]}>JPG, PNG, SVG • Max 2MB</Text>
                  </View>
                )}
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFileSelect}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    opacity: 0,
                    cursor: 'pointer',
                  }}
                />
              </View>

              {/* Divider with OR */}
              <View style={styles.dividerContainer}>
                <View style={[styles.dividerLine, { backgroundColor: colors.border }]} />
                <Text style={[styles.dividerText, { color: colors.textTertiary }]}>OR</Text>
                <View style={[styles.dividerLine, { backgroundColor: colors.border }]} />
              </View>

              {/* URL Input */}
              <TextInput
                style={[styles.urlInput, { backgroundColor: colors.secondary, borderColor: colors.border, color: colors.text }]}
                placeholder="Paste logo URL here..."
                placeholderTextColor={colors.textTertiary}
                value={newLogo.url}
                onChangeText={(text) => {
                  setNewLogo({ ...newLogo, url: text });
                  if (text) setSelectedFile(null);
                }}
                editable={!selectedFile}
              />

              {/* Add Button */}
              <TouchableOpacity 
                style={[styles.addLogoButton, { backgroundColor: colors.accent, opacity: uploadingLogo ? 0.6 : 1 }]} 
                onPress={handleAddLogo}
                disabled={uploadingLogo}
              >
                {uploadingLogo ? (
                  <ActivityIndicator color="#fff" />
                ) : (
                  <>
                    <Text style={styles.addLogoButtonIcon}>✨</Text>
                    <Text style={styles.addLogoButtonText}>Add Logo</Text>
                  </>
                )}
              </TouchableOpacity>
            </View>

            {/* Existing Logos */}
            <View style={styles.logosGrid}>
              {partnerLogos.map((logo) => (
                <View key={logo.id} style={[styles.logoCard, { ...glass, backgroundColor: colors.card, borderColor: colors.border }]}>
                  <View style={styles.logoImageContainer}>
                    <Image
                      source={{ uri: logo.url }}
                      style={styles.logoImage}
                      resizeMode="contain"
                    />
                  </View>
                  <View style={styles.logoPreview}>
                    <Text style={[styles.logoName, { color: colors.text }]}>{logo.name}</Text>
                    <Text style={[styles.logoOrder, { color: colors.textSecondary }]}>Order: {logo.order}</Text>
                  </View>
                  <View style={styles.logoActions}>
                    <TouchableOpacity
                      style={[styles.deleteButton, { backgroundColor: '#ff4444' }]}
                      onPress={() => handleDeleteLogo(logo.id)}
                    >
                      <Text style={styles.deleteButtonText}>Delete</Text>
                    </TouchableOpacity>
                  </View>
                </View>
              ))}
              {partnerLogos.length === 0 && (
                <Text style={[styles.emptyText, { color: colors.textSecondary }]}>No logos added yet</Text>
              )}
            </View>

            {/* Carousel Settings Control */}
            <View style={[styles.carouselSpeedCard, { ...glass, backgroundColor: colors.card, borderColor: colors.border }]}>
              <View style={styles.carouselSpeedHeader}>
                <View style={[styles.speedIconContainer, { backgroundColor: colors.accent }]}>
                  <Text style={styles.speedIcon}>⚙️</Text>
                </View>
                <View>
                  <Text style={[styles.carouselSpeedTitle, { color: colors.text }]}>Carousel Settings</Text>
                  <Text style={[styles.carouselSpeedSubtitle, { color: colors.textSecondary }]}>Customize logo carousel appearance</Text>
                </View>
              </View>
              
              <View style={styles.settingsGrid}>
                <View style={styles.settingItem}>
                  <Text style={[styles.settingLabel, { color: colors.text }]}>Speed (seconds)</Text>
                  <TextInput
                    style={[styles.settingInput, { backgroundColor: colors.secondary, borderColor: colors.border, color: colors.text }]}
                    placeholder="40"
                    placeholderTextColor={colors.textTertiary}
                    keyboardType="numeric"
                    value={String(carouselSpeed)}
                    onChangeText={(text) => setCarouselSpeed(parseInt(text) || 40)}
                  />
                  <Text style={[styles.settingHint, { color: colors.textTertiary }]}>20-60 recommended</Text>
                </View>

                <View style={styles.settingItem}>
                  <Text style={[styles.settingLabel, { color: colors.text }]}>Logo Size (px)</Text>
                  <TextInput
                    style={[styles.settingInput, { backgroundColor: colors.secondary, borderColor: colors.border, color: colors.text }]}
                    placeholder="120"
                    placeholderTextColor={colors.textTertiary}
                    keyboardType="numeric"
                    value={String(logoSize)}
                    onChangeText={(text) => setLogoSize(parseInt(text) || 120)}
                  />
                  <Text style={[styles.settingHint, { color: colors.textTertiary }]}>80-200 recommended</Text>
                </View>

                <View style={styles.settingItem}>
                  <Text style={[styles.settingLabel, { color: colors.text }]}>Spacing (px)</Text>
                  <TextInput
                    style={[styles.settingInput, { backgroundColor: colors.secondary, borderColor: colors.border, color: colors.text }]}
                    placeholder="60"
                    placeholderTextColor={colors.textTertiary}
                    keyboardType="numeric"
                    value={String(logoSpacing)}
                    onChangeText={(text) => setLogoSpacing(parseInt(text) || 60)}
                  />
                  <Text style={[styles.settingHint, { color: colors.textTertiary }]}>30-100 recommended</Text>
                </View>
              </View>
              
              <TouchableOpacity 
                style={[styles.updateSpeedButton, { backgroundColor: colors.accent }]} 
                onPress={handleUpdateCarouselSettings}
              >
                <Text style={styles.updateSpeedButtonText}>Update Carousel Settings</Text>
              </TouchableOpacity>
              
              <Text style={[styles.speedHint, { color: colors.textTertiary, marginTop: 12 }]}>
                Changes will apply immediately to the landing page carousel
              </Text>
            </View>
          </View>
        )}
      </View>
      
      <Toast
        visible={toast.visible}
        message={toast.message}
        type={toast.type}
        onHide={() => setToast({ ...toast, visible: false })}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    minHeight: '100vh',
    width: '100%',
    backgroundColor: '#ffffff',
    paddingTop: 100,
  },
  content: {
    flex: 1,
  },
  contentContainer: {
    padding: 20,
    width: '100%',
    paddingBottom: 60,
  },
  titleSection: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 30,
  },
  title: {
    fontSize: 32,
    fontWeight: 'bold',
    color: '#1e293b',
  },
  refreshButton: {
    backgroundColor: '#4a9eff',
    padding: 12,
    borderRadius: 8,
  },
  refreshButtonText: {
    color: '#1e293b',
    fontSize: 14,
    fontWeight: '600',
  },
  statsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 15,
    marginBottom: 30,
  },
  statCard: {
    backgroundColor: '#f8fafc',
    padding: 20,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#e2e8f0',
    minWidth: 150,
    flex: 1,
    minWidth: 0,
    alignItems: 'center',
  },
  statValue: {
    fontSize: 28,
    fontWeight: 'bold',
    color: '#4a9eff',
    marginBottom: 5,
  },
  statLabel: {
    fontSize: 12,
    color: '#64748b',
    textAlign: 'center',
  },
  section: {
    marginBottom: 30,
  },
  sectionTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#1e293b',
    marginBottom: 15,
  },
  tableContainer: {
    backgroundColor: '#f8fafc',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#e2e8f0',
    overflow: 'hidden',
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: '#ffffff',
    padding: 15,
    borderBottomWidth: 1,
    borderBottomColor: '#333',
  },
  tableHeaderText: {
    color: '#4a9eff',
    fontWeight: 'bold',
    fontSize: 12,
  },
  tableRow: {
    flexDirection: 'row',
    padding: 15,
    borderBottomWidth: 1,
    borderBottomColor: '#222',
  },
  tableCell: {
    color: '#1e293b',
    fontSize: 12,
  },
  insightCard: {
    backgroundColor: '#f8fafc',
    padding: 20,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#e2e8f0',
  },
  insightText: {
    color: '#1e293b',
    fontSize: 14,
    marginBottom: 10,
    lineHeight: 22,
  },
  insightHighlight: {
    color: '#4a9eff',
    fontWeight: 'bold',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    color: '#4a9eff',
    marginTop: 15,
    fontSize: 16,
  },
  accessDenied: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 40,
  },
  accessDeniedTitle: {
    fontSize: 48,
    marginBottom: 20,
  },
  accessDeniedText: {
    fontSize: 18,
    color: '#64748b',
    textAlign: 'center',
    marginBottom: 30,
  },
  backButton: {
    backgroundColor: '#4a9eff',
    padding: 15,
    borderRadius: 8,
  },
  backButtonText: {
    color: '#1e293b',
    fontSize: 16,
    fontWeight: '600',
  },
  // Tab Styles
  tabContainer: {
    flexDirection: 'row',
    borderRadius: 12,
    padding: 4,
    marginBottom: 30,
    borderWidth: 1,
  },
  tab: {
    flex: 1,
    paddingVertical: 12,
    paddingHorizontal: 20,
    borderRadius: 8,
    alignItems: 'center',
    transition: 'all 0.3s ease',
  },
  activeTab: {
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  tabText: {
    fontSize: 15,
    fontWeight: '600',
  },
  // Site Management Styles
  siteManagementContainer: {
    paddingTop: 20,
  },
  formCard: {
    padding: 24,
    borderRadius: 16,
    marginBottom: 30,
    borderWidth: 1,
  },
  formTitle: {
    fontSize: 20,
    fontWeight: '700',
    marginBottom: 20,
  },
  formGroup: {
    marginBottom: 16,
  },
  label: {
    fontSize: 14,
    fontWeight: '600',
    marginBottom: 8,
  },
  input: {
    borderWidth: 1,
    borderRadius: 8,
    padding: 12,
    fontSize: 15,
  },
  addButton: {
    padding: 14,
    borderRadius: 8,
    alignItems: 'center',
    marginTop: 8,
  },
  addButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600',
  },
  logosGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 20,
  },
  logoCard: {
    flex: 1,
    minWidth: 250,
    padding: 20,
    borderRadius: 12,
    borderWidth: 1,
  },
  logoImageContainer: {
    width: '100%',
    height: 100,
    marginBottom: 15,
    backgroundColor: '#f8f9fa',
    borderRadius: 8,
    padding: 10,
    justifyContent: 'center',
    alignItems: 'center',
  },
  logoImage: {
    width: '100%',
    height: '100%',
  },
  logoPreview: {
    marginBottom: 15,
  },
  logoName: {
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 4,
  },
  logoOrder: {
    fontSize: 14,
  },
  filePreview: {
    padding: 10,
    backgroundColor: '#f0f4f8',
    borderRadius: 6,
    marginTop: 10,
  },
  filePreviewText: {
    fontSize: 14,
  },
  helpText: {
    fontSize: 12,
    marginTop: 6,
  },
  logoActions: {
    flexDirection: 'row',
    gap: 10,
  },
  deleteButton: {
    padding: 10,
    borderRadius: 6,
    flex: 1,
    alignItems: 'center',
  },
  deleteButtonText: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '600',
  },
  emptyText: {
    fontSize: 16,
    textAlign: 'center',
    padding: 40,
  },
  // Visual Form Styles
  addLogoCard: {
    padding: 32,
    borderRadius: 20,
    marginBottom: 40,
    borderWidth: 1,
  },
  addLogoHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
    marginBottom: 28,
  },
  addIconContainer: {
    width: 56,
    height: 56,
    borderRadius: 16,
    justifyContent: 'center',
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.15,
    shadowRadius: 8,
    elevation: 4,
  },
  addIcon: {
    color: '#fff',
    fontSize: 32,
    fontWeight: '300',
  },
  addLogoHeaderText: {
    flex: 1,
  },
  addLogoTitle: {
    fontSize: 22,
    fontWeight: '700',
    marginBottom: 4,
  },
  addLogoSubtitle: {
    fontSize: 14,
  },
  visualFormGrid: {
    flexDirection: 'row',
    gap: 16,
    marginBottom: 20,
  },
  visualFormField: {
    flex: 1,
  },
  visualInput: {
    borderWidth: 1,
    borderRadius: 12,
    padding: 16,
    fontSize: 15,
    fontWeight: '500',
  },
  uploadArea: {
    position: 'relative',
    height: 140,
    borderRadius: 16,
    borderWidth: 2,
    borderStyle: 'dashed',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 20,
    overflow: 'hidden',
    transition: 'all 0.3s ease',
  },
  uploadPlaceholder: {
    alignItems: 'center',
    gap: 8,
  },
  uploadIcon: {
    fontSize: 40,
    marginBottom: 4,
  },
  uploadText: {
    fontSize: 15,
    fontWeight: '600',
  },
  uploadHint: {
    fontSize: 12,
  },
  uploadedFileInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingHorizontal: 20,
  },
  uploadedFileName: {
    flex: 1,
    fontSize: 14,
    fontWeight: '600',
  },
  removeFileBtn: {
    fontSize: 24,
    fontWeight: '300',
    padding: 8,
  },
  dividerContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
    marginVertical: 20,
  },
  dividerLine: {
    flex: 1,
    height: 1,
  },
  dividerText: {
    fontSize: 12,
    fontWeight: '600',
    letterSpacing: 1,
  },
  urlInput: {
    borderWidth: 1,
    borderRadius: 12,
    padding: 16,
    fontSize: 15,
    fontWeight: '500',
    marginBottom: 20,
  },
  addLogoButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    padding: 16,
    borderRadius: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.15,
    shadowRadius: 8,
    elevation: 4,
  },
  addLogoButtonIcon: {
    fontSize: 18,
  },
  addLogoButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '700',
  },
  // Carousel Speed Styles
  carouselSpeedCard: {
    padding: 28,
    borderRadius: 16,
    borderWidth: 1,
    marginTop: 40,
  },
  carouselSpeedHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
    marginBottom: 24,
  },
  speedIconContainer: {
    width: 48,
    height: 48,
    borderRadius: 12,
    justifyContent: 'center',
    alignItems: 'center',
  },
  speedIcon: {
    fontSize: 24,
  },
  carouselSpeedTitle: {
    fontSize: 18,
    fontWeight: '700',
    marginBottom: 4,
  },
  carouselSpeedSubtitle: {
    fontSize: 13,
  },
  speedControlRow: {
    flexDirection: 'row',
    gap: 12,
    marginBottom: 12,
  },
  speedInput: {
    flex: 1,
    borderWidth: 1,
    borderRadius: 12,
    padding: 14,
    fontSize: 15,
    fontWeight: '600',
  },
  updateSpeedButton: {
    paddingVertical: 14,
    paddingHorizontal: 24,
    borderRadius: 12,
    justifyContent: 'center',
    alignItems: 'center',
  },
  updateSpeedButtonText: {
    color: '#fff',
    fontSize: 15,
    fontWeight: '700',
  },
  speedHint: {
    fontSize: 12,
    fontStyle: 'italic',
  },
  settingsGrid: {
    flexDirection: 'row',
    gap: 16,
    marginBottom: 16,
    flexWrap: 'wrap',
  },
  settingItem: {
    flex: 1,
    minWidth: 150,
  },
  settingLabel: {
    fontSize: 13,
    fontWeight: '600',
    marginBottom: 8,
  },
  settingInput: {
    borderWidth: 1,
    borderRadius: 10,
    padding: 12,
    fontSize: 15,
    fontWeight: '600',
    marginBottom: 6,
  },
  settingHint: {
    fontSize: 11,
    fontStyle: 'italic',
  },
});

